import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import MoneyOffIcon from '@mui/icons-material/MoneyOff';
import PhoneIcon from '@mui/icons-material/Phone';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { Link as RouterLink } from 'react-router-dom';

interface Section {
  id: string;
  title: string;
  content: (string | { type: 'check' | 'cross'; text: string })[];
}

const SECTIONS: Section[] = [
  {
    id: 'overview',
    title: '1. Policy Overview',
    content: [
      'All refund requests must be submitted by phone so that our team can review your situation personally and ensure the most appropriate resolution.',
    ],
  },
  {
    id: 'how-to-request',
    title: '2. How to Request a Refund',
    content: [
      'To request a refund, please contact our billing support team by phone. Our team will listen to your situation, review your account, and work with you to find a fair solution.',
      'Phone: (877) 370-2220\nMonday – Friday, 8:00 AM to 5:00 PM (Eastern Time)',
      'Please have the following information available when you call:',
      '• Your full name and the email address associated with your account',
      '• A brief description of your reason for requesting a refund',
    ],
  },
  {
    id: 'individual-subscriptions',
    title: '3. Individual Subscriptions',
    content: [
      'Refund requests for individual subscriptions (annual or monthly plans) are evaluated on a case-by-case basis. We give strong consideration to requests that meet the following criteria:',
      '• The request is made within 30 days of the original purchase or most recent renewal date',
      '• The subscription has not been used to complete or claim CPE credit for any courses during the billing period',
      'Requests made after 30 days or where course access has been substantially used are generally not eligible for a full refund, though partial credits or accommodations may be offered at our discretion.',
    ],
  },
  {
    id: 'enterprise-subscriptions',
    title: '4. Enterprise & Organizational Subscriptions',
    content: [
      'Enterprise (Tenant) subscriptions are subject to the terms agreed upon at the time of purchase, which may include a separate order form or service agreement.',
      'Refund requests for enterprise subscriptions are reviewed individually and may involve additional documentation or approval steps. Please contact us as early as possible if an issue arises with an organizational plan.',
      'Seat license adjustments, plan downgrades, or early termination arrangements for enterprise accounts are handled through direct communication with our billing team.',
    ],
  },
  {
    id: 'non-refundable',
    title: '5. Non-Refundable Items',
    content: [
      'The following are generally not eligible for refunds:',
      '• Subscriptions where CPE credits have been claimed and submitted to a licensing board or professional organization',
      '• Refund requests made more than 30 days after the original purchase or renewal date',
      '• Charges that were previously disputed through a financial institution (chargebacks)',
      '• Promotional, discounted, or complimentary subscriptions provided at no cost or reduced cost',
      '• Partial billing periods: we do not prorate refunds for unused days within a billing cycle unless an exception is granted',
    ],
  },
  {
    id: 'cancellations',
    title: '6. Cancellations',
    content: [
      'Cancellations cannot be processed through the platform directly. To cancel your subscription, please contact our billing team by phone.',
      'When a cancellation is approved, your access to the platform continues through the end of the current billing period. You will not be charged at the next renewal date.',
      'Cancellations do not automatically result in a refund for the current billing period. If you also wish to request a refund, please indicate this when you call.',
    ],
  },
  {
    id: 'refund-processing',
    title: '7. Refund Processing',
    content: [
      'Approved refunds are credited back to the original payment method on file. Please allow 5–10 business days for the refund to appear on your statement, depending on your financial institution.',
      'In cases where the original payment method is no longer available, our team will work with you to arrange an alternative.',
      'You will receive a confirmation by email once your refund has been approved and processed.',
      'Auto renewal can be disabled from the billing page.'
    ],
  },
  {
    id: 'contact',
    title: '8. Contact Our Billing Team',
    content: [
      'We are here to help. If you have questions about your subscription, a charge, or this policy, please reach out to us directly:',
      'Phone: (877) 370-2220\nEmail: hello@cpacrossings.com\nMonday – Friday, 8:00 AM to 5:00 PM (Eastern Time)',
      'We strive to respond to all billing inquiries promptly and resolve issues in a manner that is fair to both you and our team.',
    ],
  },
];

export default function RefundPolicy() {
  return (
    <Stack spacing={3} sx={{ px: 3 }}>
      {/* Header */}
      <Box>
        <Typography
          variant="h4"
          sx={{
            fontSize: '32px',
            fontWeight: 400,
            lineHeight: 1.235,
            letterSpacing: '0.00735em',
            color: 'text.primary',
            mb: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <MoneyOffIcon color="primary" sx={{ fontSize: 32 }} />
          Refunds and Cancellations
        </Typography>
      </Box>

      {/* Call-to-action banner */}
      <Card
        elevation={0}
        sx={{
          border: '1px solid',
          borderColor: 'primary.light',
          borderRadius: '8px',
          bgcolor: 'primary.50',
          background: (theme) =>
            theme.palette.mode === 'dark' ? 'rgba(86, 151, 55, 0.08)' : '#e8f5e9',
        }}
      >
        <CardContent sx={{ p: 2.5 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'primary.main', mb: 0.5 }}>
                Need a refund or want to cancel?
              </Typography>
              <Typography variant="body2" color="text.secondary">
                We do not process refunds or cancellations through the website. Please call our billing team and we
                will be happy to assist you.
              </Typography>
            </Box>
            <Chip
              icon={<PhoneIcon />}
              label="(877) 370-2220"
              color="primary"
              variant="outlined"
              sx={{ borderRadius: '100px', fontWeight: 500, whiteSpace: 'nowrap' }}
            />
          </Stack>
        </CardContent>
      </Card>

      {/* Quick eligibility summary */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <Card
          elevation={0}
          sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '8px', flex: 1 }}
        >
          <CardContent sx={{ p: 2.5 }}>
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600, mb: 1.5, display: 'flex', alignItems: 'center', gap: 0.75 }}
            >
              <CheckCircleOutlineIcon color="success" fontSize="small" />
              Generally Eligible
            </Typography>
            <Stack spacing={0.75}>
              {[
                'Request within 30 days of purchase',
                'Accidental or duplicate charge',
                'Technical issue preventing access',
                'Subscription purchased in error'
              ].map((item) => (
                <Typography key={item} variant="body2" color="text.secondary" sx={{ fontSize: '13px' }}>
                  • {item}
                </Typography>
              ))}
            </Stack>
          </CardContent>
        </Card>

        <Card
          elevation={0}
          sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '8px', flex: 1 }}
        >
          <CardContent sx={{ p: 2.5 }}>
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600, mb: 1.5, display: 'flex', alignItems: 'center', gap: 0.75 }}
            >
              <HighlightOffIcon color="error" fontSize="small" />
              Generally Not Eligible
            </Typography>
            <Stack spacing={0.75}>
              {[
                'CPE credits already claimed/submitted',
                'Request after 90 days of purchase',
                'Courses completed during the period'
              ].map((item) => (
                <Typography key={item} variant="body2" color="text.secondary" sx={{ fontSize: '13px' }}>
                  • {item}
                </Typography>
              ))}
            </Stack>
          </CardContent>
        </Card>
      </Stack>

      {/* Main policy content */}
      <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '8px' }}>
        <CardContent sx={{ p: 3 }}>
          <Stack spacing={0}>
            <Divider sx={{ mb: 3 }} />

            {SECTIONS.map((section, idx) => (
              <Box key={section.id} sx={{ mb: 4 }}>
                <Stack spacing={1.25}>
                  {section.content.map((paragraph, pIdx) =>
                    typeof paragraph === 'string' ? (
                      <Typography
                        key={pIdx}
                        variant="body1"
                        sx={{
                          fontSize: '15px',
                          lineHeight: 1.65,
                          color: paragraph.startsWith('•') ? 'text.primary' : 'text.secondary',
                          pl: paragraph.startsWith('•') ? 2 : 0,
                          whiteSpace: 'pre-line',
                        }}
                      >
                        {paragraph}
                      </Typography>
                    ) : null
                  )}
                </Stack>
                {idx < SECTIONS.length - 1 && <Divider sx={{ mt: 3 }} />}
              </Box>
            ))}

            <Divider sx={{ mb: 3 }} />
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: { xs: 'flex-start', sm: 'center' },
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 1.5,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Questions about this policy?
              </Typography>
              <Button component={RouterLink} to="/app/contact" variant="outlined" size="small">
                Contact Us
              </Button>
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </Stack>
  );
}
